import { useState, useEffect } from "react";
import { SEOHead, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/theme-toggle";
import { Input } from "@/components/ui/input";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { TEST_QUOTE_SIGNUP_PATH } from "@/lib/signup-flow";
import { 
  FileText, 
  TrendingUp, 
  Clock, 
  ArrowRight,
  CheckCircle2,
  Upload,
  Sparkles,
  Camera,
  Zap,
  BarChart3,
  Shield,
  Star,
  XCircle,
  Hammer,
  Wrench,
  Paintbrush,
  Plug,
  Droplets,
  Home,
  Ruler,
  HardHat,
  Layout,
  Download,
  Users,
  Loader2,
  Brain,
  BookOpen,
  AlertCircle,
  PlusCircle,
  SlidersHorizontal,
  GitBranch,
  Wallet,
  Timer,
  Target,
  Award,
  ChevronRight,
  PoundSterling,
  LayoutTemplate,
  PenLine,
  Building2,
  Layers,
  Car,
  Warehouse,
  ChefHat,
  Columns,
  Triangle,
  Leaf,
  Building,
  Trees,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";

import logoImg from "@assets/Untitled_design_(9)_1773925932166.png";
import logoDarkImg from "@assets/Untitled_design_(10)_1773926263699.png";

const MARKET_PRICING = {
  uk: { symbol: "£",  taxLabel: "VAT",  lite: 29,  starter: 59,  pro: 99  },
  us: { symbol: "$",  taxLabel: "Tax",  lite: 39,  starter: 79,  pro: 129 },
  au: { symbol: "A$", taxLabel: "Tax",  lite: 59,  starter: 119, pro: 199 },
  ca: { symbol: "C$", taxLabel: "Tax",  lite: 49,  starter: 99,  pro: 169 },
} as const;

type Market = keyof typeof MARKET_PRICING;

const MARKET_LABELS: Record<Market, string> = {
  uk: "🇬🇧 UK",
  us: "🇺🇸 USA",
  au: "🇦🇺 Australia",
  ca: "🇨🇦 Canada",
};

// Feature flag: set to true to re-enable the market switcher bar and multi-currency pricing.
const HOMEPAGE_MARKET_ENABLED = false;

const SCREENSHOT_FEATURES = [
  {
    id: "ai-drawing",
    tag: "Auto Quote Generation",
    icon: Zap,
    title: "Auto Quote. Priced in Minutes.",
    description: "Upload your structural drawings, PDFs, site photos, or simply describe the job in plain text. Works across all project types, from large jobs like extensions, loft conversions, and garage conversions, to mid-size jobs like wall openings, bathroom renovations, and kitchen refurbs, right down to smaller works like new flooring, replastering, or garden landscaping.",
    bullets: [
      "Reads structural drawings, plans, and photos",
      "Extracts beam sizes, spans, and connection details",
      "Master Builder and Chartered Engineer validation built in feature",
      "31-category Cost Library with 462 reference rates"
    ],
    screenshot: "/screenshots/drawing-analysis.png",
    screenshotAlt: "Structural engineering drawing being analysed",
    imageLeft: false
  },
  {
    id: "template-quote",
    tag: "Template Quote",
    icon: LayoutTemplate,
    title: "Template Quote. Select Your Project Type and Get a Full Scope in Seconds.",
    description: "Choose from extensions, loft conversions, full house renovation or bathroom refurbs, and more. The Proper Tool pre-loads every relevant trade item for that project type, adjusted to your size and spec. Edit, remove, or add before you save.",
    bullets: [
      "12 residential project types covered",
      "Scope pre-loaded with all relevant trades",
      "Optional extras suggested for each project type",
      "Fully editable before saving as a quote"
    ],
    screenshot: "/screenshots/template-quote.png",
    screenshotAlt: "Template Quote project type selection grid",
    imageLeft: true
  },
  {
    id: "manual-quote",
    tag: "Manual Quote",
    icon: PenLine,
    title: "Manual Quote. Build From Scratch With Your Cost Library.",
    description: "Search 462 rated tasks across 31 trade categories and add them line by line. Ideal for bespoke or unusual jobs where no template fits. Your overridden rates apply automatically as you build.",
    bullets: [
      "Search and filter 462 cost library tasks",
      "Your overridden rates apply as you add items",
      "Group line items by trade section",
      "Save as a full quote in one click"
    ],
    screenshot: "/screenshots/manual-quote.png",
    screenshotAlt: "Manual Quote builder with cost library search panel",
    imageLeft: false
  },
  {
    id: "description-gen",
    tag: "Description Generator",
    icon: FileText,
    title: "Generate a Full, Client-Ready Project Description in One Click.",
    description: "Type a few keywords or upload your drawings and The Proper Tool writes a complete, professional scope of works description for you. Choose between a Detailed view for in-depth technical breakdowns or a user-friendly view for clean, easy-to-read summaries. Use it as the basis for your quote or send it directly to clients.",
    bullets: [
      "Works from keywords, drawings, or uploaded PDFs",
      "Generates numbered scope sections with full detail",
      "Intelligence flags highlight anything that needs your review",
      "Clear and start again when you're ready for the next job"
    ],
    screenshot: "/screenshots/description-generator.png",
    screenshotAlt: "Project Description Generator form",
    imageLeft: true
  },
  {
    id: "review-findings",
    tag: "Review Findings",
    icon: AlertCircle,
    title: "Expert Validation Flags Issues Before You Send.",
    description: "Your quote passes through a Master Builder and Chartered Engineer intelligent review feature. Pricing outliers, scope gaps, and quantity concerns are surfaced for you to accept or reject, with a suggested correction and the reasoning behind it.",
    bullets: [
      "Price outliers flagged with suggested corrections",
      "Scope gaps and missing dependencies identified",
      "Quantity concerns raised with builder-facing explanation",
      "One-click Apply or Dismiss on each finding"
    ],
    screenshot: "/screenshots/review-findings.png",
    screenshotAlt: "Review Findings panel with flagged pricing issue",
    imageLeft: false
  },
  {
    id: "suggested-items",
    tag: "Suggested Additional Items",
    icon: PlusCircle,
    title: "Catch Missing Items Before They Cost You the Job.",
    description: "Smart detection finds items that should be in the quote but aren't, skip hire for a demolition job, tanking for a wet room, fire protection for exposed steelwork. Each suggestion includes a price and a reason. You accept or dismiss.",
    bullets: [
      "Auto-detects common missing trade items",
      "Each suggestion includes a unit cost and explanation",
      "Intelligence Flags highlight scope gaps inline",
      "High-confidence items added automatically"
    ],
    screenshot: "/screenshots/suggested-items.png",
    screenshotAlt: "Suggested Additional Items with Add to Quote button",
    imageLeft: true
  },
  {
    id: "quotes-mgmt",
    tag: "Quote Management",
    icon: Layout,
    title: "All Your Quotes, Saved and Tracked in One Place.",
    description: "Every quote you generate is saved, searchable, and linked to clients. Review, convert to PDF, or pick up where you left off, all from one clean list. Share quotes with clients and keep a record of every version sent. Filter by status, search by name, and never lose a quote again.",
    bullets: [
      "Full quote history with live status tracking",
      "Search and filter across all your quotes",
      "Resume draft quotes from where you left off",
      "Share quotes with clients and track every version sent"
    ],
    screenshot: "/screenshots/quotes-list.png",
    screenshotAlt: "Quotes list page showing all saved quotes with status and actions",
    imageLeft: false
  },
  {
    id: "work-items",
    tag: "Quote Editing",
    icon: SlidersHorizontal,
    title: "Edit Any Line in Your Quote. Every Change Is Saved.",
    description: "Every line item is fully editable, price, quantity, description, and category. Whether a customer wants to add more work or you need to update something before you send, just edit the relevant lines and save. The quote stays current without starting again.",
    bullets: [
      "Edit price, quantity, unit, and description freely",
      "Add new lines or remove items at any point",
      "Re-send an updated version to the same client",
      "Section totals grouped by trade"
    ],
    screenshot: "/screenshots/work-items.png",
    screenshotAlt: "Quote editing view with editable line items",
    imageLeft: true
  },
  {
    id: "cost-library",
    tag: "Cost Library",
    icon: BookOpen,
    title: "462 Rates Across 31 Categories, All Yours to Own.",
    description: "The Cost Library holds 462 construction rates across 31 trade categories, providing the base rates your quotes draw from. Default Low, Typical, and High bands are there from day one. Set your own rate on any task and it always takes priority on future quotes.",
    bullets: [
      "462 reference tasks across 31 construction categories",
      "Your rates marked 'My Rate' and always take priority",
      "Add custom tasks not on the default list",
      "Rate changes are logged for your records"
    ],
    screenshot: "/screenshots/cost-library.png",
    screenshotAlt: "Cost Library with My Rate override highlighted",
    imageLeft: false
  },
  {
    id: "learned-adjustments",
    tag: "Learned Adjustments",
    icon: TrendingUp,
    title: "Set Your Pricing Rules Once. Future Quotes Apply Them Automatically.",
    description: "Set a multiplier or fixed addition against any trade category. If groundworks always runs higher in your area, or plastering waste exceeds the default, set the rule once and future quotes apply it automatically, so your pricing gets sharper over time.",
    bullets: [
      "Multiplier or fixed addition set per trade category",
      "Applied automatically to matching future line items",
      "Activate or deactivate any saved adjustment",
      "Builds a real price profile unique to your business"
    ],
    screenshot: "/screenshots/learned-adjustments.png",
    screenshotAlt: "Learned Adjustments table showing saved rules by trade category",
    imageLeft: true
  },
  {
    id: "pricing-intel",
    tag: "Pricing Intelligence",
    icon: Brain,
    title: "Build a Pricing Profile From the Quotes You Upload.",
    description: "Upload your previous quotes and the system builds a pricing profile from what you have actually charged. The more data you upload, the sharper it gets. Over time your Pricing Intelligence profile holds the min, median, and max rates for each trade category, with a confidence score that grows as more quotes are added.",
    bullets: [
      "Per-category price signals: min / median / max",
      "Confidence score grows as more quotes are uploaded",
      "Import historical quotes to accelerate your profile",
      "Pricing Accuracy Index tracks how well rates match your history"
    ],
    screenshot: "/screenshots/pricing-accuracy-index.png",
    screenshotAlt: "Pricing Accuracy Index gauge showing score 66",
    imageLeft: false
  },
  {
    id: "dashboard",
    tag: "Smart Dashboard",
    icon: BarChart3,
    title: "Every Quote Sent, Every Pound Earned, Every Hour Saved.",
    description: "The Smart Dashboard gives you an instant read on your quoting activity, total quotes sent, total value quoted, hours saved versus manual quoting, and money earned through referrals. Updated in real time as you work.",
    bullets: [
      "Total quotes and pipeline value tracked",
      "Time saved vs. manual quoting calculated",
      "Recent quote history with quick-edit access",
      "Money earned from referral commissions"
    ],
    screenshot: "/screenshots/dashboard-stats.png",
    screenshotAlt: "Dashboard showing Total Quotes, Time Saved, and Money Saved stats",
    imageLeft: true
  },
  {
    id: "referrals",
    tag: "Referrals & Earnings",
    icon: Wallet,
    title: "Earn a Fixed Monthly Reward for Every Builder You Refer.",
    description: "Share your unique referral code with other builders. When they subscribe, you earn £10, £15, or £20 a month based on their plan, for 12 months straight. Track every referral and request withdrawals from your Referrals dashboard.",
    bullets: [
      "£10/mo for Lite, £15/mo for Starter, £20/mo for Pro",
      "Fixed monthly reward paid for 12 months per referral",
      "Every referral tracked in real time on your board",
      "Request a withdrawal any time, paid directly to your account"
    ],
    screenshot: "/screenshots/referrals.png",
    screenshotAlt: "Referrals dashboard showing active referrals and earnings",
    imageLeft: false
  },
];

const TRADES = [
  { name: "Single-Storey Extension", icon: Home },
  { name: "Double-Storey Extension", icon: Building2 },
  { name: "Loft Conversion", icon: Layers },
  { name: "Garage Conversion", icon: Car },
  { name: "Bathroom Renovation", icon: Droplets },
  { name: "Kitchen Renovation", icon: ChefHat },
  { name: "Structural Opening", icon: Columns },
  { name: "Roofing", icon: Triangle },
  { name: "Landscaping", icon: Leaf },
  { name: "Full House Renovation", icon: Building },
  { name: "Garden Office", icon: Trees },
  { name: "General Building", icon: HardHat },
];

export default function LandingPage() {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralInput, setReferralInput] = useState("");
  const [referralValid, setReferralValid] = useState<boolean | null>(null);
  const [referralName, setReferralName] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<"lite" | "starter" | "pro">("starter");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [market, setMarket] = useState<Market>("uk");

  const { data: user } = useQuery<any>({ queryKey: ['/api/auth/user'] });
  const { data: plans } = useQuery<any[]>({ queryKey: ['/api/stripe/plans'] });

  const checkoutMutation = useMutation({
    mutationFn: async (priceId: string) => {
      const res = await apiRequest('POST', '/api/stripe/create-checkout', {
        priceId,
        source: "pricing_landing",
      });
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
      setCheckoutLoading(null);
    },
    onError: () => {
      setCheckoutLoading(null);
    },
  });

  const handlePlanClick = (tier: string) => {
    if (!user) {
      localStorage.setItem("tpt_pending_tier", tier);
      window.location.href = '/login';
      return;
    }
    const plan = plans?.find((p: any) => p.tier === tier);
    if (plan?.priceId) {
      setCheckoutLoading(tier);
      checkoutMutation.mutate(plan.priceId);
    }
  };

  useEffect(() => {
    // Cross-page anchors can be resolved before React mounts the target.
    // Scroll after commit so pricing links reliably land on the actual plans.
    if (window.location.hash === "#pricing") {
      document.getElementById("pricing")?.scrollIntoView({ block: "start" });
    }
  }, []);

  useEffect(() => {
    if (!HOMEPAGE_MARKET_ENABLED) return;
    const stored = localStorage.getItem("tpt_market") as Market | null;
    if (stored && stored in MARKET_PRICING) {
      setMarket(stored);
    }
  }, []);

  const sym = MARKET_PRICING[market].symbol;
  const referralDescription = `Share your unique referral code with other builders. When they subscribe, you earn ${sym}10, ${sym}15, or ${sym}20 a month based on their plan, for 12 months straight. Track every referral and request withdrawals from your Referrals dashboard.`;
  const referralBullets = [
    `${sym}10/mo for Lite, ${sym}15/mo for Starter, ${sym}20/mo for Pro`,
    "Fixed monthly reward paid for 12 months per referral",
    "Every referral tracked in real time on your board",
    "Request a withdrawal any time, paid directly to your account",
  ];

  const handleMarketChange = (m: Market) => {
    setMarket(m);
    if (HOMEPAGE_MARKET_ENABLED) {
      localStorage.setItem("tpt_market", m);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      localStorage.setItem("tpt_referral_code", refCode);
      setReferralCode(refCode);
      setReferralInput(refCode);
      fetch("/api/referrals/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: refCode }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            setReferralValid(true);
            setReferralName(data.referrerName);
          }
        })
        .catch(() => {});
    } else {
      const stored = localStorage.getItem("tpt_referral_code");
      if (stored) {
        setReferralCode(stored);
        setReferralInput(stored);
      }
    }
  }, []);

  const homepageSchema = [
    WEBSITE_SCHEMA,
    ORGANIZATION_SCHEMA,
    SOFTWARE_SCHEMA,
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is The Proper Tool?",
          "acceptedAnswer": { "@type": "Answer", "text": "The Proper Tool is builder quoting software built for tradespeople and construction companies. It generates professional, itemised quotes from drawings, photos or a plain text description, typically in under 10 minutes." }
        },
        {
          "@type": "Question",
          "name": "What do I need to provide to generate a quote?",
          "acceptedAnswer": { "@type": "Answer", "text": "You can upload architect's drawings or structural plans, attach site photos, or simply type a description of the job. The tool reads the project and builds a fully priced, itemised scope of works automatically." }
        },
        {
          "@type": "Question",
          "name": "What types of building work does it cover?",
          "acceptedAnswer": { "@type": "Answer", "text": "It covers all common residential project types: rear extensions, loft conversions, garage conversions, kitchen and bathroom refurbishments, structural alterations including steelwork, full house renovations, garden landscaping and more. The 31-category cost library covers all major trades." }
        },
        {
          "@type": "Question",
          "name": "How accurate are the prices?",
          "acceptedAnswer": { "@type": "Answer", "text": "Quotes are built from a 31-category cost library using current market rates and validated against scope completeness. You review and approve every line item before the quote goes to a client, so the final price always reflects your own judgement." }
        },
        {
          "@type": "Question",
          "name": "Is there a free trial?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. All new accounts get a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start." }
        },
        {
          "@type": "Question",
          "name": "How much does it cost after the trial?",
          "acceptedAnswer": { "@type": "Answer", "text": "Plans start from local pricing per month (Lite) which includes 10 quotes per month, professional PDF export, and the full cost library. The Starter plan adds Pricing Intelligence, expert validation, and 25 quotes. The Pro plan gives you 50 quotes, learned adjustments, and drawing analysis." }
        },
        {
          "@type": "Question",
          "name": "Does it produce a PDF quote I can send to clients?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every quote exports as a professionally formatted PDF showing your company details, a full line-item scope of works, pricing, and assumptions and exclusions. You can download it or send it directly from the tool." }
        },
        {
          "@type": "Question",
          "name": "Can I use it on my phone or tablet on site?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. The Proper Tool is fully web-based and works on any device. You can upload photos and start a quote on site, then review and finalise it back in the office." }
        },
        {
          "@type": "Question",
          "name": "What is the best quoting software for UK builders?",
          "acceptedAnswer": { "@type": "Answer", "text": "The Proper Tool is purpose-built quoting software for UK builders. It generates fully itemised, professionally priced quotes from drawings, photos or a plain-text description in under 10 minutes, with UK material rates, 31 trade categories and PDF export built in. Unlike generic construction software, it is designed specifically for the UK residential market and used by sole traders and small building firms across England, Scotland and Wales." }
        },
        {
          "@type": "Question",
          "name": "How long does it take to set up The Proper Tool?",
          "acceptedAnswer": { "@type": "Answer", "text": "There is no setup. Create an account, describe your first job, and your quote is ready in minutes. No spreadsheets to configure, no rate cards to build from scratch. The cost library is pre-loaded with current UK market rates across 31 categories." }
        },
        {
          "@type": "Question",
          "name": "Does The Proper Tool include UK material pricing?",
          "acceptedAnswer": { "@type": "Answer", "text": "Yes. The cost library uses current UK market rates across 31 trade categories, covering materials, labour and plant. Rates reflect realistic UK supplier and subcontractor pricing and are validated against live market data." }
        },
        {
          "@type": "Question",
          "name": "What makes The Proper Tool different from Buildxact?",
          "acceptedAnswer": { "@type": "Answer", "text": "Buildxact is a full project management and estimating platform aimed at larger contractors, with a steeper learning curve and higher price point. The Proper Tool focuses on one thing: generating professional, itemised quotes fast. It is designed for sole traders and small building firms who need accurate quotes quickly, not a full back-office system. Setup takes minutes, not days, and plans start from £29 per month." }
        },
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="The Proper Tool | Builder Quoting Software for Tradespeople"
        description="Builder quoting software for tradespeople. Generate accurate, professional construction quotes from drawings, photos or a job description in under 10 minutes."
        canonical="/"
        schema={homepageSchema}
      />
      <div className="h-full overflow-y-auto overflow-x-hidden bg-background">
      {referralValid && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-primary text-primary-foreground text-center py-2 px-4 text-sm font-medium" data-testid="banner-referral">
          You've been referred by {referralName}. Sign up and your referral will be applied automatically.
        </div>
      )}
      {/* Navigation */}
      <nav className={`fixed ${referralValid ? "top-9" : "top-0"} left-0 right-0 z-50 bg-[hsl(200,14%,25%)] backdrop-blur-md border-b border-[hsl(200,14%,20%)]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-16 md:h-20">
            <a href="/" className="flex items-center flex-shrink-0">
              <img
                src={logoDarkImg}
                alt="The Proper Tool"
                className="h-10 md:h-14 w-auto object-contain"
                fetchPriority="high"
              />
            </a>
            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-6 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <a href="#features" className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors" data-testid="link-features">
                Features
              </a>
              <a href="#how-it-works" className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors" data-testid="link-how-it-works">
                Process
              </a>
              <a href="#pricing" className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors" data-testid="link-pricing">
                Pricing
              </a>
              <a href="/blog" className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors" data-testid="link-blog">
                Blog
              </a>
              <a href="/comparisons" className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors" data-testid="link-comparisons">
                Compare
              </a>
              <div className="relative group">
                <button className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors flex items-center gap-1" data-testid="nav-solutions">
                  The App <ChevronDown className="h-3 w-3" />
                </button>
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50">
                  <div className="bg-[hsl(200,14%,22%)] border border-[hsl(200,14%,18%)] rounded-lg shadow-lg min-w-60 py-1">
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(198,15%,50%)]">Product Features</p>
                    <a href="/auto-quote" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-quoting-software">Auto Quote</a>
                    <a href="/estimating-software-for-builders" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-estimating-builders">Estimating Software</a>
                    <a href="/construction-estimating-software" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-estimating-software">Cost Estimating</a>
                    <a href="/quoting-app-for-builders" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-quoting-app">Mobile App</a>
                    <div className="border-t border-[hsl(200,14%,18%)] my-1" />
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(198,15%,50%)]">Who It's For</p>
                    <a href="/quoting-software-for-contractors" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-quoting-contractors">For Contractors</a>
                    <a href="/tradesman-quoting-tool" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-tradesman-tool">For Tradesmen</a>
                    <a href="/job-quoting-software" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-job-quoting">For Job Quoting</a>
                    <div className="border-t border-[hsl(200,14%,18%)] my-1" />
                    <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-[hsl(198,15%,50%)]">Compare</p>
                    <a href="/best-quoting-software" className="block px-4 py-2 text-sm text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors" data-testid="link-best-quoting">Compare Tools</a>
                  </div>
                </div>
              </div>
              <a href="/login" className="font-semibold text-[hsl(198,15%,78%)] hover:text-white transition-colors" data-testid="button-login">
                Log in
              </a>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <ThemeToggle />
              <Button className="hidden sm:inline-flex bg-primary text-primary-foreground font-semibold text-sm px-3 py-1 h-auto" asChild data-testid="button-get-started">
                <a href={TEST_QUOTE_SIGNUP_PATH}>Start Free Quote</a>
              </Button>
              {/* Mobile hamburger */}
              <button
                className="md:hidden flex items-center justify-center w-9 h-9 rounded-md text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,35%)] transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="button-mobile-menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[hsl(200,14%,22%)] border-t border-[hsl(200,14%,18%)]">
            <div className="px-4 py-3 space-y-1">
              {[
                { href: "#features", label: "Features", testId: "mobile-link-features" },
                { href: "#how-it-works", label: "Process", testId: "mobile-link-process" },
                { href: "#pricing", label: "Pricing", testId: "mobile-link-pricing" },
                { href: "/blog", label: "Blog", testId: "mobile-link-blog" },
                { href: "/comparisons", label: "Compare", testId: "mobile-link-compare" },
                { href: "/auto-quote", label: "Auto Quote", testId: "mobile-link-quoting-software" },
                { href: "/estimating-software-for-builders", label: "Estimating Software", testId: "mobile-link-estimating-builders" },
                { href: "/construction-estimating-software", label: "Cost Estimating", testId: "mobile-link-estimating-software" },
                { href: "/quoting-app-for-builders", label: "Mobile App", testId: "mobile-link-quoting-app" },
                { href: "/quoting-software-for-contractors", label: "For Contractors", testId: "mobile-link-quoting-contractors" },
                { href: "/tradesman-quoting-tool", label: "For Tradesmen", testId: "mobile-link-tradesman-tool" },
                { href: "/job-quoting-software", label: "For Job Quoting", testId: "mobile-link-job-quoting" },
                { href: "/best-quoting-software", label: "Compare Tools", testId: "mobile-link-best-quoting" },
                { href: "/login", label: "Log in", testId: "mobile-link-login" },
              ].map(({ href, label, testId }) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-3 rounded-lg font-semibold text-[hsl(198,15%,78%)] hover:text-white hover:bg-[hsl(200,14%,30%)] transition-colors"
                  data-testid={testId}
                >
                  {label}
                </a>
              ))}
              <div className="pt-2 pb-1">
                <a
                  href={TEST_QUOTE_SIGNUP_PATH}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center bg-primary text-primary-foreground font-semibold rounded-lg px-4 py-3 text-sm"
                  data-testid="mobile-button-start-trial"
                >
                  Start Free Quote
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className={`relative ${referralValid ? "pt-[6.25rem] md:pt-[7.25rem]" : "pt-16 md:pt-20"} bg-[hsl(200,14%,22%)] overflow-hidden`}>
        {/* Market Switcher Bar — hidden when HOMEPAGE_MARKET_ENABLED is false */}
        {HOMEPAGE_MARKET_ENABLED && (
          <div className="relative z-10 flex items-center justify-center gap-1.5 py-2 px-4 border-b border-[hsl(200,14%,28%)]">
            {(Object.keys(MARKET_PRICING) as Market[]).map((m) => (
              <button
                key={m}
                onClick={() => handleMarketChange(m)}
                className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                  market === m
                    ? "bg-[hsl(188,54%,45%)] text-white"
                    : "text-[hsl(198,15%,65%)] hover:text-white hover:bg-[hsl(200,14%,30%)]"
                }`}
              >
                {MARKET_LABELS[m]}
              </button>
            ))}
          </div>
        )}

        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "linear-gradient(hsl(188,54%,75%) 1px, transparent 1px), linear-gradient(90deg, hsl(188,54%,75%) 1px, transparent 1px)",
          backgroundSize: "40px 40px"
        }} />
        {/* Radial glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-20" style={{
          background: "radial-gradient(ellipse, hsl(188,54%,65%) 0%, transparent 70%)"
        }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-0">

          {/* Top badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[hsl(188,54%,55%)]/40 bg-[hsl(188,54%,55%)]/10 text-[hsl(188,54%,85%)] text-sm font-semibold">
              <Award className="h-4 w-4" />
              Builder Quoting Software for Tradespeople
            </div>
          </div>

          {/* Headline + subheadline */}
          <div className="text-center w-full max-w-4xl mx-auto mb-10">
            <p className="inline-block text-xs sm:text-sm font-semibold tracking-widest uppercase text-[hsl(188,54%,75%)] mb-5 px-3 py-1 rounded-full border border-[hsl(188,54%,60%)]/30 bg-[hsl(188,54%,60%)]/10">
              Built for Builders &amp; Construction Companies
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] tracking-tight text-white mb-6 text-center" data-testid="text-hero-title">
              Win more jobs.<br />
              <span className="text-[hsl(188,54%,75%)]">Quote in minutes.</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-[hsl(198,15%,68%)] max-w-2xl mx-auto leading-relaxed text-center" data-testid="text-hero-subtitle">
              Upload your drawings or describe the job. The Proper Tool builds a fully priced, professional quote ready to send in under 10 minutes.
            </p>
          </div>

          {/* 3 key value props */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-10 mb-10">
            {[
              { icon: Timer, text: "Average quote time: 8 minutes" },
              { icon: Target, text: "Market rates across 31 trades" },
              { icon: TrendingUp, text: "37% higher client win rate" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2.5 justify-center">
                <div className="w-7 h-7 rounded-full bg-[hsl(188,54%,60%)]/20 border border-[hsl(188,54%,60%)]/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-3.5 w-3.5 text-[hsl(188,54%,80%)]" />
                </div>
                <span className="text-[hsl(198,15%,78%)] text-sm font-medium">{text}</span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold gap-2 text-base h-13 w-52 justify-center shadow-lg shadow-primary/25" asChild data-testid="button-hero-start">
              <a href={TEST_QUOTE_SIGNUP_PATH}>
                Start Free Quote
                <ChevronRight className="h-4 w-4" />
              </a>
            </Button>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold gap-2 text-base h-13 w-44 justify-center shadow-lg shadow-primary/25" asChild data-testid="button-hero-pricing">
              <a href="#pricing">
                See Pricing
                <ChevronRight className="h-4 w-4" />
              </a>
            </Button>
          </div>

          {/* Social proof row */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-14">
            <span className="text-sm text-[hsl(198,12%,58%)]">Professional Quotes</span>
            <div className="hidden sm:block w-px h-4 bg-[hsl(200,14%,38%)]" />
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[hsl(188,54%,75%)] text-[hsl(188,54%,75%)]" />
                ))}
              </div>
              <span className="text-sm font-bold text-white ml-1">5.0</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-[hsl(200,14%,38%)]" />
            <span className="text-sm text-[hsl(198,12%,58%)]">Trusted by Builders</span>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
                <span className="text-2xl sm:text-3xl font-bold text-foreground dark:text-foreground" data-testid="stat-quotes">8 mins</span>
              </div>
              <div className="text-sm text-muted-foreground">Average Quote Time</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
                <span className="text-2xl sm:text-3xl font-bold text-foreground dark:text-foreground" data-testid="stat-value">{sym}10M+</span>
              </div>
              <div className="text-sm text-muted-foreground">Quote Value Created</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
                <span className="text-2xl sm:text-3xl font-bold text-foreground dark:text-foreground" data-testid="stat-time">12+ hrs</span>
              </div>
              <div className="text-sm text-muted-foreground">Saved Per Week</div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-3">
                <span className="text-2xl sm:text-3xl font-bold text-foreground dark:text-foreground" data-testid="stat-rate">37%</span>
              </div>
              <div className="text-sm text-muted-foreground">Higher Client Win Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento + Feature Strip Section */}
      <section className="relative bg-[hsl(200,14%,22%)] overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "linear-gradient(hsl(188,54%,75%) 1px, transparent 1px), linear-gradient(90deg, hsl(188,54%,75%) 1px, transparent 1px)",
          backgroundSize: "40px 40px"
        }} />
        {/* Radial glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-20" style={{
          background: "radial-gradient(ellipse, hsl(188,54%,65%) 0%, transparent 70%)"
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-0">
          {/* Bento Grid */}
          <div className="grid grid-cols-12 gap-4 max-w-5xl mx-auto">

            {/* Card 1 — wide top: Quote ready */}
            <Card className="col-span-12 sm:col-span-8 bg-[hsl(188,54%,58%)]/15 border-[hsl(188,54%,55%)]/25 overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-[hsl(188,54%,80%)]">
                    <Zap className="h-4 w-4" />
                    <span className="font-bold text-xs uppercase tracking-wider">Auto Quote Generation</span>
                  </div>
                  <Badge className="bg-[hsl(188,54%,55%)]/15 text-[hsl(188,54%,80%)] border-[hsl(188,54%,55%)]/25 border text-[10px] px-2 py-0.5">Ready</Badge>
                </div>
                {/* Progress steps */}
                <div className="space-y-2 mb-4">
                  {[
                    { label: "Drawing Uploaded", done: true },
                    { label: "Structural Elements Extracted", done: true },
                    { label: "Expert Validation Built In Feature", done: true },
                    { label: "Pricing Applied From Cost Library", done: true },
                  ].map((step) => (
                    <div key={step.label} className="flex items-center gap-2.5">
                      <div className="w-4 h-4 rounded-full bg-[hsl(188,54%,55%)]/20 border border-[hsl(188,54%,55%)]/30 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="h-2.5 w-2.5 text-[hsl(188,54%,80%)]" />
                      </div>
                      <span className="text-xs text-[hsl(198,15%,72%)]">{step.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between bg-[hsl(188,54%,55%)]/10 rounded-lg px-4 py-3 border border-[hsl(188,54%,55%)]/20">
                  <div>
                    <div className="text-[10px] text-[hsl(198,12%,55%)] uppercase tracking-wider mb-0.5">Total Quote Value</div>
                    <div className="text-2xl font-bold text-white">{sym}15,990</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-[hsl(198,12%,55%)] uppercase tracking-wider mb-0.5">Line Items</div>
                    <div className="text-2xl font-bold text-[hsl(188,54%,75%)]">15</div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-primary/20 text-[hsl(188,54%,80%)] border border-[hsl(188,54%,55%)]/30 rounded-md text-xs font-semibold h-8 px-3 select-none">
                    <Download className="h-3 w-3" />
                    Export PDF
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2 — narrow top: Time saved */}
            <Card className="col-span-12 sm:col-span-4 bg-[hsl(188,54%,58%)]/15 border-[hsl(188,54%,55%)]/25 overflow-hidden">
              <CardContent className="p-5 h-full flex flex-col justify-between">
                <div className="flex items-center gap-2 text-[hsl(188,54%,80%)] mb-3">
                  <Timer className="h-4 w-4" />
                  <span className="font-bold text-xs uppercase tracking-wider">Time Saved</span>
                </div>
                <div>
                  <div className="text-5xl font-bold text-white mb-1">8<span className="text-2xl font-semibold text-[hsl(198,12%,55%)] ml-1">min</span></div>
                  <div className="text-xs text-[hsl(198,12%,55%)]">vs. 3–4 hrs manually</div>
                </div>
                <div className="mt-4 flex items-end gap-1 h-10">
                  {[30, 55, 40, 70, 50, 90, 75, 100].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t-sm" style={{
                      height: `${h}%`,
                      background: i >= 5 ? "hsl(188,54%,65%)" : "hsla(188,54%,45%,0.35)"
                    }} />
                  ))}
                </div>
                <div className="text-[10px] text-[hsl(198,12%,48%)] mt-1">Quotes this month</div>
              </CardContent>
            </Card>

            {/* Card 3 — line items preview */}
            <Card className="col-span-12 sm:col-span-5 bg-[hsl(188,54%,58%)]/15 border-[hsl(188,54%,55%)]/25 overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-[hsl(188,54%,80%)] mb-4">
                  <Layout className="h-4 w-4" />
                  <span className="font-bold text-xs uppercase tracking-wider">Line Items</span>
                </div>
                <div className="space-y-0">
                  <div className="flex justify-between text-[10px] text-[hsl(198,12%,50%)] uppercase pb-2 border-b border-[hsl(200,14%,33%)]">
                    <span>Description</span><span>Price</span>
                  </div>
                  {[
                    { name: "Blockwork Cavity Wall", price: `${sym}2,800`, cat: "Blockwork" },
                    { name: "Concrete Strip Foundation", price: `${sym}1,650`, cat: "Foundation" },
                    { name: "1st Fix Joinery Roof Structure", price: `${sym}2,100`, cat: "Carpentry" },
                    { name: "First Fix Electrical Works", price: `${sym}1,450`, cat: "Electrics" },
                    { name: "RSJ Steel Installation", price: `${sym}1,800`, cat: "Steel" },
                  ].map((item, i) => (
                    <div key={i} className={`flex justify-between items-center py-2 ${i < 4 ? "border-b border-[hsl(200,14%,32%)]" : ""}`}>
                      <div>
                        <div className="text-xs text-[hsl(198,15%,82%)]">{item.name}</div>
                        <div className="text-[10px] text-[hsl(198,12%,50%)]">{item.cat}</div>
                      </div>
                      <span className="text-xs font-semibold text-[hsl(188,54%,80%)]">{item.price}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 text-xs text-[hsl(198,12%,50%)]">
                    <span>+ 10 more items</span>
                    <span className="font-bold text-white">{sym}15,990</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 4 — validation flags */}
            <Card className="col-span-12 sm:col-span-4 bg-[hsl(188,54%,58%)]/15 border-[hsl(188,54%,55%)]/25 overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-[hsl(188,54%,80%)] mb-4">
                  <Shield className="h-4 w-4" />
                  <span className="font-bold text-xs uppercase tracking-wider">Expert Review Feature</span>
                </div>
                <div className="space-y-2.5">
                  {[
                    { label: "Master Builder Layer Check", ok: true },
                    { label: "Chartered Engineer Layer Check", ok: true },
                    { label: "Scope Gaps Reviewed", ok: true },
                    { label: "Companion Trades Identified", ok: true },
                    { label: "Wastage Allowances Applied", ok: true },
                    { label: "Suggested Additional Items", ok: false },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      {item.ok
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(188,54%,80%)] flex-shrink-0" />
                        : <AlertCircle className="h-3.5 w-3.5 text-[hsl(188,54%,70%)] flex-shrink-0" />
                      }
                      <span className="text-xs text-[hsl(198,15%,72%)]">{item.label}</span>
                      {(item as any).action && (
                        <span className="text-[10px] text-[hsl(188,54%,80%)] font-semibold border border-[hsl(188,54%,55%)]/30 rounded px-1.5 py-0.5 bg-[hsl(188,54%,55%)]/10">{(item as any).action}</span>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Card 5 — win rate stat */}
            <Card className="col-span-12 sm:col-span-3 bg-[hsl(188,54%,58%)]/15 border-[hsl(188,54%,55%)]/25 overflow-hidden">
              <CardContent className="p-5 flex flex-col h-full justify-between">
                <div className="flex items-center gap-2 text-[hsl(188,54%,80%)] mb-2">
                  <TrendingUp className="h-4 w-4" />
                  <span className="font-bold text-xs uppercase tracking-wider">Win Rate</span>
                </div>
                <div>
                  <div className="text-5xl font-bold text-white">37<span className="text-2xl text-[hsl(188,54%,75%)]">%</span></div>
                  <div className="text-xs text-[hsl(198,12%,58%)] mt-1">higher vs. manual quotes</div>
                </div>
                <div className="mt-3 h-1.5 w-full bg-[hsl(200,14%,35%)] rounded-full overflow-hidden">
                  <div className="h-full bg-[hsl(188,54%,65%)] w-[72%]" />
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Feature highlight strip */}
          <div className="mt-8 border-t border-[hsl(200,14%,32%)] pt-6 pb-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
              {[
                { icon: Upload, label: "Upload Any Drawing" },
                { icon: Brain, label: "Instant Pricing" },
                { icon: Shield, label: "Expert Validation" },
                { icon: BookOpen, label: "Rate Library (462 tasks)" },
                { icon: PoundSterling, label: "Your Rates Remembered" },
                { icon: FileText, label: "Pro PDF In One Click" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-2 text-center">
                  <div className="w-9 h-9 rounded-xl bg-[hsl(200,14%,35%)] border border-[hsl(200,14%,35%)] flex items-center justify-center">
                    <Icon className="h-4 w-4 text-[hsl(188,54%,75%)]" />
                  </div>
                  <span className="text-xs text-[hsl(198,12%,58%)] leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Screenshot Showcase */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="text-features-title">
              See Exactly What You Get
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Every feature shown below is live in the app right now. No mockups.
            </p>
          </div>

          <div className="space-y-16 md:space-y-24 lg:space-y-32">
            {SCREENSHOT_FEATURES.map((feat) => (
              <div
                key={feat.id}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}
                data-testid={`feature-${feat.id}`}
              >
                {/* Text side */}
                <div className={`space-y-6 ${feat.imageLeft ? "lg:order-2" : ""}`}>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                    <feat.icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold text-primary">{feat.tag}</span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold leading-snug">{feat.title}</h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">{feat.id === "referrals" ? referralDescription : feat.description}</p>
                  <ul className="space-y-3">
                    {(feat.id === "referrals" ? referralBullets : feat.bullets).map((b, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-base">{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Button size="lg" className="bg-primary text-primary-foreground font-semibold gap-2 mt-2" asChild>
                    <a href={TEST_QUOTE_SIGNUP_PATH}>
                      Start Free Quote
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </Button>
                </div>

                {/* Screenshot side */}
                <div className={`${feat.imageLeft ? "lg:order-1" : ""}`}>
                  <div className="relative rounded-2xl overflow-hidden border shadow-xl bg-card">
                    <img
                      src={feat.screenshot}
                      alt={feat.screenshotAlt}
                      className="w-full h-auto object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Trades Grid Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Built for Builders &amp; Construction Companies
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TRADES.map((trade) => (
              <div key={trade.name} className="bg-card border rounded-xl p-6 flex items-center gap-4 hover-elevate">
                <trade.icon className="h-6 w-6 text-muted-foreground" />
                <span className="font-medium">{trade.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="text-how-title">
              Quote in 3 Simple Steps
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From project photos to professional PDF in minutes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Upload Your Project</h3>
              <p className="text-muted-foreground">
                Upload photos, architectural drawings, or structural plans. Prefer text? Just type a description of the work and we'll take it from there.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">Auto Quote Generation</h3>
              <p className="text-muted-foreground">
                Our quoting engine analyses your project through a 5-step pipeline with expert validation, generating detailed line items with accurate 2025/2026 pricing.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Send Professional Quote</h3>
              <p className="text-muted-foreground">
                Review, adjust if needed, and send a branded PDF quote with terms and payment schedule.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="scroll-mt-24 py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="text-pricing-title">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your business. All plans include a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className={`relative hover-elevate cursor-pointer transition-all duration-200 ${selectedPlan === "lite" ? "border-primary border-2 shadow-lg" : ""}`} onClick={() => setSelectedPlan("lite")}>
              <CardContent className="pt-8 pb-8">
                <h3 className="text-lg font-semibold mb-2">Lite</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{MARKET_PRICING[market].symbol}{MARKET_PRICING[market].lite}</span>
                  <span className="text-muted-foreground">/month + {MARKET_PRICING[market].taxLabel}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">Perfect for sole traders getting started.</p>
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    10 Auto quotes per month
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Unlimited Template &amp; Manual quotes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Professional PDF export
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Cost Library with 31 categories
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Email support
                  </li>
                </ul>
                <Button variant={selectedPlan === "lite" ? "default" : "outline"} className="w-full" onClick={(e) => { e.stopPropagation(); handlePlanClick('starter'); }} disabled={checkoutLoading === 'starter'} data-testid="button-starter">
                  {checkoutLoading === 'starter' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
                </Button>
              </CardContent>
            </Card>
            
            <Card className={`relative hover-elevate cursor-pointer transition-all duration-200 ${selectedPlan === "starter" ? "border-primary border-2 shadow-lg" : ""}`} onClick={() => setSelectedPlan("starter")}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground font-semibold">Most Popular</Badge>
              </div>
              <CardContent className="pt-8 pb-8">
                <h3 className="text-lg font-semibold mb-2">Starter</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{MARKET_PRICING[market].symbol}{MARKET_PRICING[market].starter}</span>
                  <span className="text-muted-foreground">/month + {MARKET_PRICING[market].taxLabel}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">For growing businesses wanting more power.</p>
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    25 Auto quotes per month
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Unlimited Template &amp; Manual quotes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Pricing Intelligence system
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Expert validation pipeline
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Regional pricing
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Priority support
                  </li>
                </ul>
                <Button variant={selectedPlan === "starter" ? "default" : "outline"} className="w-full font-semibold" onClick={(e) => { e.stopPropagation(); handlePlanClick('pro'); }} disabled={checkoutLoading === 'pro'} data-testid="button-pro">
                  {checkoutLoading === 'pro' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
                </Button>
              </CardContent>
            </Card>
            
            <Card className={`relative hover-elevate cursor-pointer transition-all duration-200 ${selectedPlan === "pro" ? "border-primary border-2 shadow-lg" : ""}`} onClick={() => setSelectedPlan("pro")}>
              <CardContent className="pt-8 pb-8">
                <h3 className="text-lg font-semibold mb-2">Pro</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">{MARKET_PRICING[market].symbol}{MARKET_PRICING[market].pro}</span>
                  <span className="text-muted-foreground">/month + {MARKET_PRICING[market].taxLabel}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">For established businesses with high quote volume.</p>
                <ul className="space-y-3 mb-8 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    50 Auto quotes per month
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Unlimited Template &amp; Manual quotes
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Everything in Starter
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Learned Adjustments
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Drawing analysis
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                    Dedicated support
                  </li>
                </ul>
                <Button variant={selectedPlan === "pro" ? "default" : "outline"} className="w-full" onClick={(e) => { e.stopPropagation(); handlePlanClick('team'); }} disabled={checkoutLoading === 'team'} data-testid="button-team">
                  {checkoutLoading === 'team' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start Free Trial'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            Frequently asked questions
          </h2>
          <p className="text-muted-foreground text-center mb-12 text-lg">
            Everything you need to know about The Proper Tool.
          </p>
          <div className="space-y-4">
            {[
              {
                q: "What is The Proper Tool?",
                a: "The Proper Tool is builder quoting software built for tradespeople and construction companies. It generates professional, itemised quotes from drawings, photos or a plain text description, typically in under 10 minutes."
              },
              {
                q: "What do I need to provide to generate a quote?",
                a: "You can upload architect's drawings or structural plans, attach site photos, or simply type a description of the job. The tool reads the project and builds a fully priced, itemised scope of works automatically."
              },
              {
                q: "What types of building work does it cover?",
                a: "It covers all common residential project types: rear extensions, loft conversions, garage conversions, kitchen and bathroom refurbishments, structural alterations including steelwork, full house renovations, garden landscaping and more. The 31-category cost library covers all major trades."
              },
              {
                q: "How accurate are the prices?",
                a: "Quotes are built from a 31-category cost library using current market rates and validated against scope completeness. You review and approve every line item before the quote goes to a client, so the final price always reflects your own judgement."
              },
              {
                q: "Is there a free trial?",
                a: "Yes. All new accounts get a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes and access to all features. No credit card is required to start."
              },
              {
                q: "How much does it cost after the trial?",
                a: `Plans start from ${MARKET_PRICING[market].symbol}${MARKET_PRICING[market].lite} per month (Lite) which includes 10 quotes per month, professional PDF export, and the full cost library. The Starter plan at ${MARKET_PRICING[market].symbol}${MARKET_PRICING[market].starter} per month adds Pricing Intelligence, expert validation, and 25 quotes. The Pro plan at ${MARKET_PRICING[market].symbol}${MARKET_PRICING[market].pro} per month gives you 50 quotes, learned adjustments, and drawing analysis.`
              },
              {
                q: "Does it produce a PDF quote I can send to clients?",
                a: "Yes. Every quote exports as a professionally formatted PDF showing your company details, a full line-item scope of works, pricing, and assumptions and exclusions. You can download it or send it directly from the tool."
              },
              {
                q: "Can I use it on my phone or tablet on site?",
                a: "Yes. The Proper Tool is fully web-based and works on any device. You can upload photos and start a quote on site, then review and finalise it back in the office."
              },
              {
                q: "What is the best quoting software for UK builders?",
                a: "The Proper Tool is purpose-built quoting software for UK builders. It generates fully itemised, professionally priced quotes from drawings, photos or a plain-text description in under 10 minutes, with UK material rates, 31 trade categories and PDF export built in. Unlike generic construction software, it is designed specifically for the UK residential market and used by sole traders and small building firms across England, Scotland and Wales."
              },
              {
                q: "How long does it take to set up The Proper Tool?",
                a: "There is no setup. Create an account, describe your first job, and your quote is ready in minutes. No spreadsheets to configure, no rate cards to build from scratch. The cost library is pre-loaded with current UK market rates across 31 categories."
              },
              {
                q: "Does The Proper Tool include UK material pricing?",
                a: "Yes. The cost library uses current UK market rates across 31 trade categories, covering materials, labour and plant. Rates reflect realistic UK supplier and subcontractor pricing and are validated against live market data."
              },
              {
                q: "What makes The Proper Tool different from Buildxact?",
                a: "Buildxact is a full project management and estimating platform aimed at larger contractors, with a steeper learning curve and higher price point. The Proper Tool focuses on one thing: generating professional, itemised quotes fast. It is designed for sole traders and small building firms who need accurate quotes quickly, not a full back-office system. Setup takes minutes, not days, and plans start from £29 per month."
              },
            ].map((item, i) => (
              <details key={i} className="group border border-border rounded-xl">
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer font-semibold text-base sm:text-lg select-none list-none">
                  {item.q}
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-open:rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <div className="px-6 pb-5 text-muted-foreground leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
          <div className="text-center mt-10">
            <a href="/blog" className="text-primary hover:underline font-medium text-sm">
              Read our blog articles and guides for builders
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="bg-[hsl(200,14%,30%)] relative">
        {/* SVG wave top */}
        <div className="w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full block" style={{ height: "80px" }}>
            <path d="M0,0 C240,80 480,0 720,48 C960,80 1200,16 1440,56 L1440,0 L0,0 Z" className="fill-background" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Start Winning More Work.
          </h2>
          <p className="text-xl text-[hsl(198,15%,78%)] mb-8 max-w-2xl mx-auto">
            Generate professional, itemised quotes in minutes and win more jobs with faster, smarter proposals built for construction.
          </p>

          <div className="flex justify-center">
            <Button size="lg" className="bg-primary text-primary-foreground font-semibold h-12 px-10" asChild data-testid="button-cta-start">
              <a href={TEST_QUOTE_SIGNUP_PATH}>Start Free Quote</a>
            </Button>
          </div>
          <p className="text-sm text-[hsl(198,12%,65%)] mt-4">
            14-day free trial. No credit card required.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img src={logoImg} alt="The Proper Tool" className="h-14 w-auto object-contain dark:hidden" />
                <img src={logoDarkImg} alt="The Proper Tool" className="h-14 w-auto object-contain hidden dark:block" />
              </div>
              <p className="text-sm text-muted-foreground max-w-sm">
                Quoting software built specifically for builders and construction firms. Generate professional quotes in minutes, not hours.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-foreground transition-colors">How It Works</a></li>
                <li><a href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</a></li>
                <li><a href="/comparisons" className="hover:text-foreground transition-colors">Compare</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">The App</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/auto-quote" className="hover:text-foreground transition-colors">Auto Quote</a></li>
                <li><a href="/estimating-software-for-builders" className="hover:text-foreground transition-colors">Estimating Software</a></li>
                <li><a href="/construction-estimating-software" className="hover:text-foreground transition-colors">Cost Estimating</a></li>
                <li><a href="/best-quoting-software" className="hover:text-foreground transition-colors">Compare Tools</a></li>
                <li><a href="/quoting-app-for-builders" className="hover:text-foreground transition-colors">Mobile App</a></li>
                <li><a href="/quoting-software-for-contractors" className="hover:text-foreground transition-colors">For Contractors</a></li>
                <li><a href="/tradesman-quoting-tool" className="hover:text-foreground transition-colors">For Tradesmen</a></li>
                <li><a href="/job-quoting-software" className="hover:text-foreground transition-colors">For Job Quoting</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/terms" className="hover:text-foreground transition-colors">Terms and Conditions</a></li>
                <li><a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a></li>
                <li><a href="/cookies" className="hover:text-foreground transition-colors">Cookie Policy</a></li>
                <li><a href="/referral-terms" className="hover:text-foreground transition-colors">Referral Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} The Proper Tool. All rights reserved.
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>Your data is secure and encrypted</span>
            </div>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
}
